package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginecardview.layouts.WA_PLATZ_TYP
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginepalettenkonto.PalettenkontoApi
import org.example.project.engineservice.ServiceApi
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.engineslot.loadSlotBookings
import org.example.project.engineslot.SLOT_EXTRA_BOOKING_DATE
import org.example.project.engineslot.slotBookingArtCode
import org.example.project.engineslot.slotHasBookingData
import org.example.project.engineslot.slotLadenTouren
import org.example.project.engineslot.slotLiefernBelege
import org.example.project.service.GlobalManager
import org.example.project.service.InfrastrukturApi
import org.example.project.service.LoginService
import org.example.project.service.SnapApi
import org.example.project.service.YardApi
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.gridTemplateColumns
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

private const val SYSTEM_DASHBOARD_SCOPE_MONTH = "Monat"
private const val SYSTEM_DASHBOARD_SCOPE_DAY = "Aktueller Tag"
private const val SYSTEM_DASHBOARD_SCOPE_YEAR = "Jahr"

@Composable
fun CUSSystemDashboardScreen() {
    Style(CUSCeoDashboardStyles)

    var loading by remember { mutableStateOf(true) }
    var data by remember { mutableStateOf(SystemDashboardData()) }
    var scope by remember { mutableStateOf(SYSTEM_DASHBOARD_SCOPE_MONTH) }
    var selectedStandort by remember { mutableStateOf("") }
    val refreshVersion = LocalScreenRefreshVersion.current

    LaunchedEffect(refreshVersion, selectedStandort) {
        loading = true
        data = loadSystemDashboardData(selectedStandort)
        loading = false
    }

    val tiles = systemDashboardTiles(data, scope)
    val stateTiles = systemDashboardStateTiles(data)

    Div({ classes(CUSCeoDashboardStyles.screenRoot) }) {
        Div({
            classes(CUSCeoDashboardStyles.stickyTop)
            style {
                property("flex", "1 1 auto")
                property("min-height", "0")
                property("justify-content", "center")
            }
        }) {
            Div({
                style {
                    property("display", "flex")
                    property("justify-content", "center")
                    property("align-items", "center")
                    property("gap", "8px")
                    property("padding", "0 0 18px 0")
                    property("flex-wrap", "wrap")
                }
            }) {
                systemDashboardStandortOptions(data.standorte).forEach { option ->
                    LumaticaButton(
                        option.label,
                        onClick = { selectedStandort = option.key },
                        variant = LumaticaButtonVariant.State,
                        isActive = selectedStandort == option.key
                    )
                }
                systemDashboardScopes().forEach { option ->
                    LumaticaButton(
                        option,
                        onClick = { scope = option },
                        variant = LumaticaButtonVariant.State,
                        isActive = scope == option
                    )
                }
            }
            Div({
                classes(CUSCeoDashboardStyles.tileScrollHost)
                style {
                    property("overflow-y", "auto")
                    property("height", "100%")
                    property("min-height", "0")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("justify-content", "center")
                    property("align-items", "center")
                }
            }) {
                Div({
                    classes(CUSCeoDashboardStyles.grid)
                    style {
                        gridTemplateColumns("repeat(5, 240px)")
                        property("width", "1710px")
                        property("max-width", "100%")
                        property("margin-left", "auto")
                        property("margin-right", "auto")
                        property("margin-bottom", "0")
                    }
                }) {
                    tiles.forEach { tile ->
                        SystemDashboardTile(
                            tile = tile,
                            loading = loading
                        )
                    }
                }
                Div({
                    style {
                        property("height", "38px")
                    }
                })
                Div({
                    classes(CUSCeoDashboardStyles.divider)
                    style {
                        property("max-width", "1710px")
                        property("margin-left", "auto")
                        property("margin-right", "auto")
                    }
                })
                Div({
                    style {
                        property("height", "38px")
                    }
                })
                Div({
                    classes(CUSCeoDashboardStyles.grid)
                    style {
                        gridTemplateColumns("repeat(5, 240px)")
                        property("width", "1710px")
                        property("max-width", "100%")
                        property("margin-left", "auto")
                        property("margin-right", "auto")
                        property("margin-bottom", "0")
                    }
                }) {
                    stateTiles.forEach { tile ->
                        SystemDashboardTile(
                            tile = tile,
                            loading = loading
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun SystemDashboardTile(tile: All, loading: Boolean) {
    Div({
        classes(CUSCeoDashboardStyles.tile)
    }) {
        Div({ classes(CUSCeoDashboardStyles.iconSlot) }) {
            Img(src = tile.icon, attrs = { classes(CUSCeoDashboardStyles.icon) })
        }
        Div({ classes(CUSCeoDashboardStyles.textColumn) }) {
            Div({ classes(CUSCeoDashboardStyles.tileLabel) }) { Text(tile.label) }
            Div {
                Div({ classes(CUSCeoDashboardStyles.value) }) { Text(if (loading) "..." else tile.bezeichnung) }
                if (tile.beschreibung.orEmpty().isNotBlank()) {
                    Div({ classes(CUSCeoDashboardStyles.tileHint) }) { Text(tile.beschreibung.orEmpty()) }
                }
            }
        }
    }
}

private data class SystemDashboardData(
    val standorte: List<All> = emptyList(),
    val weDay: Int = 0,
    val weMonth: Int = 0,
    val weYear: Int = 0,
    val weOpenDay: Int = 0,
    val weOpenMonth: Int = 0,
    val weOpenYear: Int = 0,
    val waDay: Int = 0,
    val waMonth: Int = 0,
    val waYear: Int = 0,
    val waOpenDay: Int = 0,
    val waOpenMonth: Int = 0,
    val waOpenYear: Int = 0,
    val vorgaengeDay: Int = 0,
    val vorgaengeMonth: Int = 0,
    val vorgaengeYear: Int = 0,
    val vorgaengeOpenDay: Int = 0,
    val vorgaengeOpenMonth: Int = 0,
    val vorgaengeOpenYear: Int = 0,
    val tauschpartner: Int = 0,
    val palAusgangDay: Int = 0,
    val palAusgangMonth: Int = 0,
    val palAusgangYear: Int = 0,
    val palEingangDay: Int = 0,
    val palEingangMonth: Int = 0,
    val palEingangYear: Int = 0,
    val palBewegungenDay: Int = 0,
    val palBewegungenMonth: Int = 0,
    val palBewegungenYear: Int = 0,
    val activeSnaps: Int = 0,
    val slotsDay: Int = 0,
    val slotsMonth: Int = 0,
    val slotsYear: Int = 0,
    val supportDay: Int = 0,
    val supportMonth: Int = 0,
    val supportYear: Int = 0,
    val crDay: Int = 0,
    val crMonth: Int = 0,
    val crYear: Int = 0,
    val aktiveVerladungHeute: Int = 0,
    val beendeteVerladungHeute: Int = 0,
    val geplanteVerladungHeute: Int = 0,
    val aktiveEntladungHeute: Int = 0,
    val beendeteEntladungHeute: Int = 0,
    val geplanteEntladungHeute: Int = 0,
    val kontrollierteAuftraegeDay: Int = 0,
    val kontrollierteAuftraegeMonth: Int = 0,
    val kontrollierteAuftraegeYear: Int = 0,
    val waAuftraegeDay: Int = 0,
    val waAuftraegeMonth: Int = 0,
    val waAuftraegeYear: Int = 0,
    val belegteTore: Int = 0,
    val freieTore: Int = 0,
    val belegteWaPlaetze: Int = 0,
    val freieWaPlaetze: Int = 0,
    val belegteParkplaetze: Int = 0,
    val freieParkplaetze: Int = 0
)

private suspend fun loadSystemDashboardData(selectedStandort: String): SystemDashboardData {
    val firmenId = systemDashboardFirmenId()
    val userId = LoginService.getId().trim()
    val dayKey = systemDashboardCurrentDayKey()
    val monthKey = systemDashboardCurrentMonthKey()
    val yearKey = monthKey.take(4)
    val standorte = runCatching { KarteiApi.loadStandorte() }.getOrDefault(emptyList())
        .sortedBy { it.standort.ifBlank { it.id }.lowercase() }
    val weRowsAll = runCatching { YardApi.loadWeLkwListe(firmenId) }.getOrDefault(emptyList())
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val waRowsAll = runCatching { YardApi.loadWaLkwListe(firmenId) }.getOrDefault(emptyList())
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val weRows = weRowsAll.filter { it.zustand != ZustandManager.STORNIERT }
    val waRows = waRowsAll.filter { it.zustand != ZustandManager.STORNIERT }
    val waAuftraege = runCatching { YardApi.loadWaListe(firmenId) }.getOrDefault(emptyList())
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val palRows = runCatching { PalettenkontoApi.loadAuskunft(firmenId, "") }.getOrDefault(emptyList())
        .filter { it.source == "system" }
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val support = runCatching { ServiceApi.loadSupport("company") }.getOrDefault(emptyList())
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val crs = runCatching { ServiceApi.loadCr("company") }.getOrDefault(emptyList())
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val slots = runCatching { loadSlotBookings(firmenId) }.getOrDefault(emptyList())
        .filter { systemDashboardSlotIsBookedOrder(it) }
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val tore = runCatching { InfrastrukturApi.loadTore(firmenId) }.getOrDefault(emptyList())
        .filter { it.gesperrt != true }
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val waPlaetze = runCatching { InfrastrukturApi.loadPlaetzeFiltered(All(typ = WA_PLATZ_TYP)) }.getOrDefault(emptyList())
        .filter { it.aktiv != false }
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val parkplaetze = runCatching { InfrastrukturApi.loadParkplaetze(firmenId) }.getOrDefault(emptyList())
        .filter { it.gesperrt != true }
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    val offeneWaStandorte = runCatching { YardApi.loadWaStandorteFiltered(All()) }.getOrDefault(emptyList())
        .filter { it.zustand < ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT }
        .filter { systemDashboardMatchesStandort(it, selectedStandort) }
    return SystemDashboardData(
        standorte = standorte,
        weDay = weRows.count { systemDashboardDayKey(it) == dayKey },
        weMonth = weRows.count { systemDashboardMonthKey(it) == monthKey },
        weYear = weRows.count { systemDashboardYearKey(it) == yearKey },
        weOpenDay = weRows.count { systemDashboardAuftragOffen(it) && systemDashboardDayKey(it) == dayKey },
        weOpenMonth = weRows.count { systemDashboardAuftragOffen(it) && systemDashboardMonthKey(it) == monthKey },
        weOpenYear = weRows.count { systemDashboardAuftragOffen(it) && systemDashboardYearKey(it) == yearKey },
        waDay = waRows.count { systemDashboardDayKey(it) == dayKey },
        waMonth = waRows.count { systemDashboardMonthKey(it) == monthKey },
        waYear = waRows.count { systemDashboardYearKey(it) == yearKey },
        waOpenDay = waRows.count { systemDashboardAuftragOffen(it) && systemDashboardDayKey(it) == dayKey },
        waOpenMonth = waRows.count { systemDashboardAuftragOffen(it) && systemDashboardMonthKey(it) == monthKey },
        waOpenYear = waRows.count { systemDashboardAuftragOffen(it) && systemDashboardYearKey(it) == yearKey },
        vorgaengeDay = (weRows + waRows).count { systemDashboardDayKey(it) == dayKey },
        vorgaengeMonth = (weRows + waRows).count { systemDashboardMonthKey(it) == monthKey },
        vorgaengeYear = (weRows + waRows).count { systemDashboardYearKey(it) == yearKey },
        vorgaengeOpenDay = (weRows + waRows).count { systemDashboardAuftragOffen(it) && systemDashboardDayKey(it) == dayKey },
        vorgaengeOpenMonth = (weRows + waRows).count { systemDashboardAuftragOffen(it) && systemDashboardMonthKey(it) == monthKey },
        vorgaengeOpenYear = (weRows + waRows).count { systemDashboardAuftragOffen(it) && systemDashboardYearKey(it) == yearKey },
        tauschpartner = runCatching { PalettenkontoApi.loadPartner(firmenId) }.getOrDefault(emptyList()).size,
        palAusgangDay = palRows.filter { systemDashboardDayKey(it) == dayKey }.sumOf { systemDashboardIntExtra(it, "ausgang") },
        palAusgangMonth = palRows.filter { systemDashboardMonthKey(it) == monthKey }.sumOf { systemDashboardIntExtra(it, "ausgang") },
        palAusgangYear = palRows.filter { systemDashboardYearKey(it) == yearKey }.sumOf { systemDashboardIntExtra(it, "ausgang") },
        palEingangDay = palRows.filter { systemDashboardDayKey(it) == dayKey }.sumOf { systemDashboardIntExtra(it, "eingang") },
        palEingangMonth = palRows.filter { systemDashboardMonthKey(it) == monthKey }.sumOf { systemDashboardIntExtra(it, "eingang") },
        palEingangYear = palRows.filter { systemDashboardYearKey(it) == yearKey }.sumOf { systemDashboardIntExtra(it, "eingang") },
        palBewegungenDay = palRows.filter { systemDashboardDayKey(it) == dayKey }.sumOf { systemDashboardIntExtra(it, "eingang") + systemDashboardIntExtra(it, "ausgang") },
        palBewegungenMonth = palRows.filter { systemDashboardMonthKey(it) == monthKey }.sumOf { systemDashboardIntExtra(it, "eingang") + systemDashboardIntExtra(it, "ausgang") },
        palBewegungenYear = palRows.filter { systemDashboardYearKey(it) == yearKey }.sumOf { systemDashboardIntExtra(it, "eingang") + systemDashboardIntExtra(it, "ausgang") },
        activeSnaps = runCatching { SnapApi.loadPartners(userId, firmenId) }.getOrDefault(emptyList()).size,
        slotsDay = slots.count { systemDashboardDayKey(it) == dayKey },
        slotsMonth = slots.count { systemDashboardMonthKey(it) == monthKey },
        slotsYear = slots.count { systemDashboardYearKey(it) == yearKey },
        supportDay = support.count { systemDashboardDayKey(it) == dayKey },
        supportMonth = support.count { systemDashboardMonthKey(it) == monthKey },
        supportYear = support.count { systemDashboardYearKey(it) == yearKey },
        crDay = crs.count { systemDashboardDayKey(it) == dayKey },
        crMonth = crs.count { systemDashboardMonthKey(it) == monthKey },
        crYear = crs.count { systemDashboardYearKey(it) == yearKey },
        aktiveVerladungHeute = waRowsAll.count { systemDashboardDayKey(it) == dayKey && systemDashboardAuftragAktiv(it) },
        beendeteVerladungHeute = waRowsAll.count { systemDashboardDayKey(it) == dayKey && it.zustand >= ZustandManager.ABGESCHLOSSEN },
        geplanteVerladungHeute = waRowsAll.count { systemDashboardDayKey(it) == dayKey && it.zustand != ZustandManager.STORNIERT },
        aktiveEntladungHeute = weRowsAll.count { systemDashboardDayKey(it) == dayKey && systemDashboardAuftragAktiv(it) },
        beendeteEntladungHeute = weRowsAll.count { systemDashboardDayKey(it) == dayKey && it.zustand >= ZustandManager.ABGESCHLOSSEN },
        geplanteEntladungHeute = weRowsAll.count { systemDashboardDayKey(it) == dayKey && it.zustand != ZustandManager.STORNIERT },
        kontrollierteAuftraegeDay = (weRowsAll + waRowsAll).count { systemDashboardAuftragKontrolliert(it) && systemDashboardDayKey(it) == dayKey },
        kontrollierteAuftraegeMonth = (weRowsAll + waRowsAll).count { systemDashboardAuftragKontrolliert(it) && systemDashboardMonthKey(it) == monthKey },
        kontrollierteAuftraegeYear = (weRowsAll + waRowsAll).count { systemDashboardAuftragKontrolliert(it) && systemDashboardYearKey(it) == yearKey },
        waAuftraegeDay = waAuftraege.count { systemDashboardDayKey(it) == dayKey },
        waAuftraegeMonth = waAuftraege.count { systemDashboardMonthKey(it) == monthKey },
        waAuftraegeYear = waAuftraege.count { systemDashboardYearKey(it) == yearKey },
        belegteTore = tore.count { it.belegt == true },
        freieTore = tore.count { it.belegt != true },
        belegteWaPlaetze = waPlaetze.count { platz -> platz.belegt == true || offeneWaStandorte.any { it.platzcode == platz.platzcode } },
        freieWaPlaetze = waPlaetze.count { platz -> platz.belegt != true && offeneWaStandorte.none { it.platzcode == platz.platzcode } },
        belegteParkplaetze = parkplaetze.count { it.belegt == true },
        freieParkplaetze = parkplaetze.count { it.belegt != true }
    )
}

private fun systemDashboardTiles(data: SystemDashboardData, scope: String): List<All> =
    listOf(
        systemDashboardRatioPeriodTile("WE_OFFEN", "WE offen", "images/we.svg", scope, data.weOpenDay, data.weDay, data.weOpenMonth, data.weMonth, data.weOpenYear, data.weYear),
        systemDashboardRatioPeriodTile("WA_OFFEN", "WA offen", "images/wa.svg", scope, data.waOpenDay, data.waDay, data.waOpenMonth, data.waMonth, data.waOpenYear, data.waYear),
        systemDashboardFixedTile("TAUSCHPARTNER", "Tauschpartner", "images/palauskunft.svg", data.tauschpartner),
        systemDashboardTile("PAL_AUSGANG", "Pal Ausgang", "images/wa.svg", scope, data.palAusgangDay, data.palAusgangMonth, data.palAusgangYear),
        systemDashboardTile("PAL_EINGANG", "Pal Eingang", "images/we.svg", scope, data.palEingangDay, data.palEingangMonth, data.palEingangYear),
        systemDashboardTile("PAL_BEWEGUNGEN", "Palettenbewegungen", "images/order.svg", scope, data.palBewegungenDay, data.palBewegungenMonth, data.palBewegungenYear),
        systemDashboardFixedTile("SNAPS", "Aktive Snaps", "images/domain.svg", data.activeSnaps),
        systemDashboardTile("SLOTS", "Gebuchte Slot-Aufträge", "images/slotview.svg", scope, data.slotsDay, data.slotsMonth, data.slotsYear),
        systemDashboardTile("SUPPORT", "Support", "images/supportauskunft.svg", scope, data.supportDay, data.supportMonth, data.supportYear),
        systemDashboardTile("CR", "CR", "images/crinfo.svg", scope, data.crDay, data.crMonth, data.crYear)
    )

private fun systemDashboardStateTiles(data: SystemDashboardData): List<All> =
    listOf(
        systemDashboardRatioTile("AKTIVE_VERLADUNG", "Aktive Verladung", "images/wa.svg", data.aktiveVerladungHeute, data.geplanteVerladungHeute),
        systemDashboardRatioTile("BEENDETE_VERLADUNG", "Beendete Verladung", "images/wa.svg", data.beendeteVerladungHeute, data.geplanteVerladungHeute),
        systemDashboardRatioTile("AKTIVE_ENTLADUNG", "Aktive Entladung", "images/we.svg", data.aktiveEntladungHeute, data.geplanteEntladungHeute),
        systemDashboardRatioTile("BEENDETE_ENTLADUNG", "Beendete Entladung", "images/we.svg", data.beendeteEntladungHeute, data.geplanteEntladungHeute),
        systemDashboardRatioTile("BELEGTE_TORE", "Belegte Tore", "images/toruebersicht.svg", data.belegteTore, data.belegteTore + data.freieTore),
        systemDashboardRatioTile("FREIE_TORE", "Freie Tore", "images/toruebersicht.svg", data.freieTore, data.belegteTore + data.freieTore),
        systemDashboardRatioTile("BELEGTE_WA_PLAETZE", "Belegte WA Plätze", "images/wavorbereitung.svg", data.belegteWaPlaetze, data.belegteWaPlaetze + data.freieWaPlaetze),
        systemDashboardRatioTile("FREIE_WA_PLAETZE", "Freie WA Plätze", "images/wavorbereitung.svg", data.freieWaPlaetze, data.belegteWaPlaetze + data.freieWaPlaetze),
        systemDashboardRatioTile("BELEGTE_PARKPLAETZE", "Belegte Parkplätze", "images/info.svg", data.belegteParkplaetze, data.belegteParkplaetze + data.freieParkplaetze),
        systemDashboardRatioTile("FREIE_PARKPLAETZE", "Freie Parkplätze", "images/info.svg", data.freieParkplaetze, data.belegteParkplaetze + data.freieParkplaetze)
    )

private fun systemDashboardTile(
    key: String,
    label: String,
    icon: String,
    scope: String,
    dayValue: Int,
    monthValue: Int,
    yearValue: Int
): All {
    val value = when (scope) {
        SYSTEM_DASHBOARD_SCOPE_DAY -> dayValue
        SYSTEM_DASHBOARD_SCOPE_YEAR -> yearValue
        else -> monthValue
    }
    return All(key = key, label = label, icon = icon, bezeichnung = value.toString(), beschreibung = "")
}

private fun systemDashboardRatioPeriodTile(
    key: String,
    label: String,
    icon: String,
    scope: String,
    dayValue: Int,
    dayTotal: Int,
    monthValue: Int,
    monthTotal: Int,
    yearValue: Int,
    yearTotal: Int
): All {
    val pair = when (scope) {
        SYSTEM_DASHBOARD_SCOPE_DAY -> dayValue to dayTotal
        SYSTEM_DASHBOARD_SCOPE_YEAR -> yearValue to yearTotal
        else -> monthValue to monthTotal
    }
    return All(key = key, label = label, icon = icon, bezeichnung = "${pair.first}/${pair.second.coerceAtLeast(pair.first)}", beschreibung = "")
}

private fun systemDashboardFixedTile(key: String, label: String, icon: String, value: Int): All =
    All(key = key, label = label, icon = icon, bezeichnung = value.toString(), beschreibung = "")

private fun systemDashboardRatioTile(key: String, label: String, icon: String, value: Int, total: Int): All =
    All(key = key, label = label, icon = icon, bezeichnung = "${value}/${total.coerceAtLeast(value)}", beschreibung = "")

private fun systemDashboardScopes(): List<String> =
    listOf(SYSTEM_DASHBOARD_SCOPE_DAY, SYSTEM_DASHBOARD_SCOPE_MONTH, SYSTEM_DASHBOARD_SCOPE_YEAR)

private fun systemDashboardStandortOptions(standorte: List<All>): List<All> =
    listOf(All(key = "", label = "Alle")) +
        standorte
            .filter { it.id.isNotBlank() }
            .distinctBy { it.id }
            .map { standort ->
                All(
                    key = standort.id,
                    label = standort.standort.ifBlank { "Standort ${standort.id}" }
                )
            }

private fun systemDashboardFirmenId(): String =
    LoginService.getUser()?.firmenId.orEmpty().trim().ifBlank { GlobalManager.currentFirmenId.trim() }

private fun systemDashboardIntExtra(row: All, key: String): Int =
    when (key) {
        "eingang" -> row.eingang
        "ausgang" -> row.ausgang
        else -> ""
    }.toString().toIntOrNull() ?: 0

private fun systemDashboardMatchesStandort(row: All, selectedStandort: String): Boolean {
    val standort = selectedStandort.trim()
    if (standort.isBlank()) return true
    return row.standort == standort ||
        row.slotStandortId == standort ||
        row.slotYardStandortKey == standort ||
        row.waStandortId == standort
}

private fun systemDashboardSlotIsBookedOrder(row: All): Boolean {
    if (row.zustand == ZustandManager.Slot.FREI || row.zustand == ZustandManager.Slot.GESPERRT) return false
    if (slotHasBookingData(row)) return true
    return when (slotBookingArtCode(row)) {
        SLOT_BEREICH_WE -> slotLiefernBelege(row).any { it.lieferscheinnummer.isNotBlank() }
        SLOT_BEREICH_WA -> slotLadenTouren(row).any { it.lieferscheinnummer.isNotBlank() }
        else -> row.slotLiefernBelege.isNotBlank() || row.slotLadenTouren.isNotBlank()
    }
}

private fun systemDashboardAuftragKontrolliert(row: All): Boolean =
    row.kontrolliert == true || row.kontrolliertWE == true || row.zustand >= ZustandManager.KONTROLLIERT

private fun systemDashboardAuftragAktiv(row: All): Boolean =
    row.zustand == ZustandManager.IN_VERLADUNG || row.zustand == ZustandManager.TEILVERLADEN || row.zustand == ZustandManager.TEILENTLADEN

private fun systemDashboardAuftragOffen(row: All): Boolean =
    row.zustand < ZustandManager.ABGESCHLOSSEN && row.zustand != ZustandManager.STORNIERT

private fun systemDashboardMonthKey(row: All): String =
    systemDashboardMonthKeyFromText(row.monat)
        .ifBlank { systemDashboardMonthKeyFromText(row.uhrzeitAbgeschlossen) }
        .ifBlank { systemDashboardMonthKeyFromText(row.bearbeitetAm) }
        .ifBlank { systemDashboardMonthKeyFromText(row.erstelltAm) }
        .ifBlank { systemDashboardMonthKeyFromText(row.datum) }
        .ifBlank { systemDashboardMonthKeyFromText(row.verladedatum) }
        .ifBlank { systemDashboardMonthKeyFromText(row.datum) }

private fun systemDashboardDayKey(row: All): String =
    systemDashboardDayKeyFromText(row.datum)
        .ifBlank { systemDashboardDayKeyFromText(row.verladedatum) }
        .ifBlank { systemDashboardDayKeyFromText(row.datum) }
        .ifBlank { systemDashboardDayKeyFromText(row.uhrzeitAbgeschlossen) }
        .ifBlank { systemDashboardDayKeyFromText(row.bearbeitetAm) }
        .ifBlank { systemDashboardDayKeyFromText(row.erstelltAm) }

private fun systemDashboardYearKey(row: All): String =
    systemDashboardMonthKey(row).take(4)

private fun systemDashboardMonthKeyFromText(value: String): String {
    val text = value.trim()
    if (text.length >= 7 && text[4] == '_') return text.take(7)
    if (text.length >= 7 && text[4] == '-') return text.take(7).replace("-", "_")
    val parts = text.split(".", "/", "-")
    return when {
        parts.size >= 3 && parts[2].length == 4 -> "${parts[2]}_${parts[1].padStart(2, '0')}"
        parts.size >= 2 && parts[0].length == 4 -> "${parts[0]}_${parts[1].padStart(2, '0')}"
        else -> ""
    }
}

private fun systemDashboardDayKeyFromText(value: String): String {
    val text = value.trim()
    if (text.length >= 10 && text[4] == '-' && text[7] == '-') return text.take(10)
    val parts = text.split(".", "/", "-")
    return when {
        parts.size >= 3 && parts[2].length == 4 -> "${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}"
        parts.size >= 3 && parts[0].length == 4 -> "${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}"
        else -> ""
    }
}

private fun systemDashboardCurrentDayKey(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    return "${now.year}-${now.monthNumber.toString().padStart(2, '0')}-${now.dayOfMonth.toString().padStart(2, '0')}"
}

private fun systemDashboardCurrentMonthKey(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    return "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
}
